import React, { useState } from 'react';
import { Table, Label, Icon, Checkbox } from 'semantic-ui-react';
import moment from 'moment';
import { formatCurrency } from '../../utils/commonutils';

/**
 * Table component to display hygienist production records
 * @param {Array} records - All hygienist records
 * @param {boolean} showOnlyUnallocated - If true, filter to show only unallocated records
 */
const UnallocatedHygienistTable = ({ records, showOnlyUnallocated = false }) => {
    const [hideZeros, setHideZeros] = useState(false);

    if (!records || records.length === 0) {
        return null;
    }

    // Helper to check if a provider abbreviation is a hygienist
    const isHygienist = (abbr) => {
        if (!abbr) return false;
        const upper = abbr.toUpperCase().trim();
        return upper.includes('HYG') || upper.includes('RDH');
    };

    // Helper to determine if record is unallocated
    const isUnallocated = (record) => {
        if (!record.Treat_prov) return true;
        
        const providers = record.Treat_prov.split(/[|,]/).map(p => p.trim()).filter(Boolean);
        
        if (providers.length === 1 && providers[0] === record.Provider) {
            return true;
        }
        
        const nonHygProviders = providers.filter(p => !isHygienist(p));
        if (nonHygProviders.length > 1) {
            return true;
        }
        
        return false;
    };

    // Helper to check if record has non-zero values
    const hasNonZeroValue = (record) => {
        return (record.amt && record.amt !== 0) || (record.bCare && record.bCare !== 0);
    };

    // Helper to get display reason/status for record
    const getRecordStatus = (record) => {
        if (!record.Treat_prov) return { text: 'No Provider', color: 'orange' };
        
        const providers = record.Treat_prov.split(/[|,]/).map(p => p.trim()).filter(Boolean);
        
        if (providers.length === 1 && providers[0] === record.Provider) {
            return { text: 'HYG as Treat Prov', color: 'orange' };
        }
        
        const nonHygProviders = providers.filter(p => !isHygienist(p));
        if (nonHygProviders.length > 1) {
            return { text: 'Multiple Providers', color: 'orange' };
        }
        
        return { text: 'Allocated', color: 'green' };
    };

    // Filter records based on props and checkbox
    let displayRecords = showOnlyUnallocated ? records.filter(isUnallocated) : records;
    
    // Apply zero filter if checkbox is checked and showing unallocated
    if (showOnlyUnallocated && hideZeros) {
        displayRecords = displayRecords.filter(hasNonZeroValue);
    }

    const showStatusColumn = !showOnlyUnallocated;

    // Count zeros for display
    const unallocatedRecords = records.filter(isUnallocated);
    const zeroCount = unallocatedRecords.filter(r => !hasNonZeroValue(r)).length;

    if (displayRecords.length === 0) {
        return (
            <>
                {showOnlyUnallocated && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontSize: '0.85em', color: '#666' }}>
                            <Icon name="info circle" />
                            Records where hygienist production was not properly allocated to a treating provider
                        </span>
                        <Checkbox 
                            label={`Hide zeros (${zeroCount} records with $0)`}
                            checked={hideZeros}
                            onChange={(e, { checked }) => setHideZeros(checked)}
                        />
                    </div>
                )}
                <p style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                    <Icon name="check circle" color="green" />
                    {hideZeros 
                        ? 'No non-zero unallocated records found.'
                        : 'No unallocated records found. All hygienist production has been properly allocated.'
                    }
                </p>
            </>
        );
    }

    const sortedRecords = [...displayRecords].sort((a, b) => 
        new Date(a.ProcDate) - new Date(b.ProcDate)
    );
    const totalAmt = displayRecords.reduce((sum, r) => sum + (r.amt || 0), 0);
    const totalBCare = displayRecords.reduce((sum, r) => sum + (r.bCare || 0), 0);

    return (
        <>
            {showOnlyUnallocated && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.85em', color: '#666' }}>
                        <Icon name="info circle" />
                        Records where hygienist production was not properly allocated to a treating provider
                    </span>
                    <Checkbox 
                        label={`Hide zeros (${zeroCount} records with $0)`}
                        checked={hideZeros}
                        onChange={(e, { checked }) => setHideZeros(checked)}
                    />
                </div>
            )}
            {!showOnlyUnallocated && (
                <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '10px' }}>
                    <Icon name="info circle" />
                    <Label color="orange" size="mini">Orange</Label> Unallocated | 
                    <Label color="green" size="mini">Green</Label> Properly allocated
                </p>
            )}
            <Table celled striped size="small">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                        <Table.HeaderCell>Patient #</Table.HeaderCell>
                        <Table.HeaderCell>Hygienist</Table.HeaderCell>
                        <Table.HeaderCell>Treating Provider</Table.HeaderCell>
                        {showStatusColumn && <Table.HeaderCell>Status</Table.HeaderCell>}
                        <Table.HeaderCell>Practice</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">bCare</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {sortedRecords.map((record, idx) => {
                        const status = getRecordStatus(record);
                        const isUnalloc = status.color === 'orange';
                        return (
                            <Table.Row key={`hyg-${idx}`} warning={isUnalloc} positive={!isUnalloc && showStatusColumn}>
                                <Table.Cell>
                                    {moment(record.ProcDate).format('MM/DD/YYYY')}
                                </Table.Cell>
                                <Table.Cell>{record.PatNum}</Table.Cell>
                                <Table.Cell>{record.Provider}</Table.Cell>
                                <Table.Cell>
                                    <Label color={status.color} size="tiny">
                                        {record.Treat_prov || 'None'}
                                    </Label>
                                </Table.Cell>
                                {showStatusColumn && (
                                    <Table.Cell>
                                        <Label basic color={status.color} size="tiny">
                                            {status.text}
                                        </Label>
                                    </Table.Cell>
                                )}
                                <Table.Cell>{record.practiceName || record.practice}</Table.Cell>
                                <Table.Cell textAlign="right">
                                    {formatCurrency(record.amt || 0)}
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                    {formatCurrency(record.bCare || 0)}
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>

                <Table.Footer>
                    <Table.Row>
                        <Table.HeaderCell colSpan={showStatusColumn ? 6 : 5}>
                            <strong>Total ({displayRecords.length} records)</strong>
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            <strong>{formatCurrency(totalAmt)}</strong>
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            <Label color="blue" size="small">
                                <strong>{formatCurrency(totalBCare)}</strong>
                            </Label>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
        </>
    );
};

export default UnallocatedHygienistTable;
